import { IPoint2d } from '@/editor/math/IPoint2d';
import BaseModel2d from './BaseModel2d';
import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import {
  ArrowHelper,
  BufferGeometry,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Sphere,
  Vector3
} from 'three';
import { pixelRatio2d } from '@/editor/math/const';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { ITextModelInfo, TextModel } from './textModel';
import { globalStores } from '@/editor/stores';

export interface IMeasureRulerInfo {
  start: IPoint2d;
  end: IPoint2d;
  editable?: boolean;
  arrow?: boolean;
  // 箭头默认从start -> end，且位置位于线段末端
  reverseArrow?: boolean;
  measuring?: boolean;
}

type TMeasureRulerChangeCallback = (value: number, rulerInfo: IMeasureRulerInfo) => void;

const RULER_COLOR = {
  standard: 0x262829,
  drawing: 0xdf4545
} as const;

export class MeasureRulerModel extends BaseModel2d {
  private _modelColor: number = RULER_COLOR.standard;
  private _rulerInfo: IMeasureRulerInfo | null = null;
  private _textInfos: ITextModelInfo[] = [];
  private _textModel = new TextModel({ onCommit: this.handleInputChange.bind(this) });
  private _onChange: TMeasureRulerChangeCallback = (_value: number, _rulerInfo: IMeasureRulerInfo) => {};

  constructor(options?: { onChange?: TMeasureRulerChangeCallback }) {
    super();
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
    this.setDisplayOrder(ENTITY_RENDER_ORDER.ruler);

    if (options?.onChange) {
      this._onChange = options.onChange;
    }
  }

  updateModel(rulerInfo: IMeasureRulerInfo) {
    this.clear();
    if (!rulerInfo) return;

    this._modelColor = rulerInfo.measuring ? RULER_COLOR.drawing : RULER_COLOR.standard;
    this._rulerInfo = rulerInfo;
    this._createArrowModel();
    this._createLineModel(rulerInfo.editable);
    this._textModel.createModel(this._textInfos);
  }

  private _createArrowModel() {
    if (!this._rulerInfo?.arrow) return;

    const start = new Vector3(this._rulerInfo.start.x / pixelRatio2d, this._rulerInfo.start.y / pixelRatio2d, 0);
    const end = new Vector3(this._rulerInfo.end.x / pixelRatio2d, this._rulerInfo.end.y / pixelRatio2d, 0);
    const direction = this._rulerInfo.reverseArrow
      ? new Vector3().subVectors(end, start).normalize().negate()
      : new Vector3().subVectors(end, start).normalize();
    const headLength = 0.24;
    const headWidth = 0.14;
    // 箭头起点向后偏移，避免箭头超出终点
    const arrowStart = (this._rulerInfo.reverseArrow ? start : end).clone().addScaledVector(direction, -headLength);
    const arrowHelper = new ArrowHelper(direction, arrowStart, headLength, this._modelColor, headLength, headWidth);

    this._modelGroup.add(arrowHelper);
  }

  private _createLineModel(editable?: boolean) {
    if (!this._rulerInfo) return;
    const pointSize = 6; // 线段和两端的辅助线共6个点
    const assistLineOffsetValue = 120; // 辅助线长度
    const lineGmt = new BufferGeometry();
    const lineFloat32Array = new Float32Array(pointSize * 2);
    const lineSeg = new LineSeg2d(this._rulerInfo.start, this._rulerInfo.end);
    lineFloat32Array.set(getRulerSegPoints(lineSeg, assistLineOffsetValue), 0);
    this._textInfos.push({
      position: { x: (lineSeg.start.x + lineSeg.end.x) / 2, y: (lineSeg.start.y + lineSeg.end.y) / 2 },
      id: 'measure',
      value: Point2dUtils.distanceTo(lineSeg.start, lineSeg.end).toFixed(0),
      editable: editable,
      trapFocus: false
    });
    lineGmt.setAttribute('position', new Float32BufferAttribute(lineFloat32Array, 2));

    // 由于没有传入z值，需要手动添加boundingsphere，防止报错
    lineGmt.boundingSphere = new Sphere(new Vector3(0, 0, 0), 1);
    const line = new LineSegments(
      lineGmt,
      new LineBasicMaterial({ color: this._modelColor, userData: { shared: true } })
    );
    line.frustumCulled = false;
    this._modelGroup.add(line);
  }

  private handleInputChange(id: string, value: number) {
    if (!id || !this._rulerInfo) return;

    this._onChange(value, this._rulerInfo);
  }

  override clear() {
    this._rulerInfo = null;
    this._modelColor = RULER_COLOR.standard;
    this._textInfos.length = 0;
    this._textModel.clear();
    super.clear();
  }

  override dispose() {
    this.clear();
    this._textModel.dispose();
    super.dispose();
  }
}

function getRulerSegPoints(lineSeg: LineSeg2d, assistLineOffsetValue: number) {
  const verticalDirection = lineSeg.getPerpendicularDirectionVector();
  const points = [];

  // 起点到终点连线
  let start = lineSeg.start;
  let end = lineSeg.end;
  points[0] = start.x / pixelRatio2d;
  points[1] = start.y / pixelRatio2d;
  points[2] = end.x / pixelRatio2d;
  points[3] = end.y / pixelRatio2d;

  // 起点辅助线
  start = Point2dUtils.addDistance(lineSeg.start, assistLineOffsetValue, verticalDirection);
  end = Point2dUtils.addDistance(lineSeg.start, -assistLineOffsetValue, verticalDirection);
  points[4] = start.x / pixelRatio2d;
  points[5] = start.y / pixelRatio2d;
  points[6] = end.x / pixelRatio2d;
  points[7] = end.y / pixelRatio2d;

  // 终点辅助线
  start = Point2dUtils.addDistance(lineSeg.end, assistLineOffsetValue, verticalDirection);
  end = Point2dUtils.addDistance(lineSeg.end, -assistLineOffsetValue, verticalDirection);
  points[8] = start.x / pixelRatio2d;
  points[9] = start.y / pixelRatio2d;
  points[10] = end.x / pixelRatio2d;
  points[11] = end.y / pixelRatio2d;

  return points;
}
